import React, { useState, useMemo } from "react";
import {
  Header,
  Container,
  SpaceBetween,
  Table,
  Pagination,
  TextFilter,
  Spinner,
  Box,
  Select,
  SelectProps,
} from "@cloudscape-design/components";
import { PAGE_SIZE_OPTIONS } from "../constants";
import type { TimeGranularity } from "./detailHelpers";
import type { ProgramDetailData, RequestStatus, DetailOtdcRow, DetailOutputHitRateRow } from "../../../redux/types/programTypes";

const TIME_LABEL: Record<TimeGranularity, string> = { day: "Day", week: "Week", month: "Month" };

export type ProdRow = DetailOutputHitRateRow;

export type ShipRow = DetailOtdcRow;

const formatQty = (value: number | null | undefined): string => {
  return value != null ? value.toLocaleString() : "-";
};

const formatRate = (value: number | null | undefined): string => {
  return value != null ? `${(value * 100).toFixed(2)}%` : "-";
};

export const ProductionShipmentDetail: React.FC<{
  showOnly?: "production" | "shipment";
  detailData?: ProgramDetailData | null;
  detailStatus?: RequestStatus;
  timeGranularity?: TimeGranularity;
}> = ({ showOnly, detailData = null, detailStatus = "idle", timeGranularity = "week" }) => {
  const [prodPage, setProdPage] = useState(1);
  const [shipPage, setShipPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [expandedProdItems, setExpandedProdItems] = useState<ReadonlyArray<ProdRow>>([]);
  const [selectedProdPageSize, setSelectedProdPageSize] = useState<SelectProps.Option>(PAGE_SIZE_OPTIONS[0] as SelectProps.Option);
  const [selectedShipPageSize, setSelectedShipPageSize] = useState<SelectProps.Option>(PAGE_SIZE_OPTIONS[0] as SelectProps.Option);
  const PROD_PAGE_SIZE = parseInt(selectedProdPageSize.value || "10", 10);
  const SHIP_PAGE_SIZE = parseInt(selectedShipPageSize.value || "10", 10);

  const tableRows = (detailData?.table || []) as (ProdRow[] | ShipRow[]);

  // Build parent-child mapping for Production table
  // Children are matched to parents by parent_program + cm + cm_location + stage (all four fields)
  // Each parent row is independently expandable with its own matching children
  const { prodDisplayItems, prodChildrenMap } = useMemo(() => {
    const data = tableRows as ProdRow[];
    // Key: "parentProgram__cm__cm_location__stage" -> children rows
    const childrenMap = new Map<string, ProdRow[]>();
    // Track existing parent keys
    const existingParentKeys = new Set<string>();

    // Identify existing parents
    data.forEach((row) => {
      if (!row.parent_program) {
        existingParentKeys.add(`${row.program}__${row.cm}__${row.cm_location}__${row.stage}`);
      }
    });

    // Gather all children grouped by full parent key (parent_program + cm + cm_location + stage)
    data.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}__${row.stage}`;
        const existing = childrenMap.get(parentKey) || [];
        existing.push(row);
        childrenMap.set(parentKey, existing);
      }
    });

    // Build display items (top-level rows)
    const displayItems: ProdRow[] = [];
    data.forEach((row) => {
      if (!row.parent_program) {
        displayItems.push(row);
      }
    });

    // Create virtual parent rows for orphan children (parent_program not found in data)
    const createdVirtualParents = new Set<string>();
    data.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}__${row.stage}`;
        if (!existingParentKeys.has(parentKey) && !createdVirtualParents.has(parentKey)) {
          createdVirtualParents.add(parentKey);
          const virtualParent: ProdRow = {
            program: row.parent_program,
            parent_program: null,
            cm: row.cm,
            cm_location: row.cm_location,
            stage: row.stage,
            type: row.type,
            plan: null,
            actual: null,
            gap: null,
            cum: null,
            hit_rate: null,
          };
          displayItems.push(virtualParent);
        }
      }
    });

    return { prodDisplayItems: displayItems, prodChildrenMap: childrenMap };
  }, [tableRows]);


  // Filtered production data (all parent-level rows shown; children provided via expandableRows)
  const filteredProdDisplay = useMemo(() => {
    if (!searchText.trim()) return prodDisplayItems;
    const q = searchText.toLowerCase();
    return prodDisplayItems.filter((r) => {
      const rowKey = `${r.program}__${r.cm}__${r.cm_location}__${r.stage}`;
      return (
        (r.program || "").toLowerCase().includes(q) ||
        `${r.cm}-${r.cm_location}`.toLowerCase().includes(q) ||
        (r.stage || "").toLowerCase().includes(q) ||
        (prodChildrenMap.get(rowKey) || []).some((child) =>
          (child.program || "").toLowerCase().includes(q) ||
          `${child.cm}-${child.cm_location}`.toLowerCase().includes(q)
        )
      );
    });
  }, [prodDisplayItems, prodChildrenMap, searchText]);

  // Filtered shipment data (flat list, no hierarchy)
  const filteredShipData = useMemo(() => {
    const data = tableRows as ShipRow[];
    if (!searchText.trim()) return data;
    const q = searchText.toLowerCase();
    return data.filter((r) =>
      (r.program || "").toLowerCase().includes(q) ||
      `${r.cm}-${r.cm_location}`.toLowerCase().includes(q)
    );
  }, [tableRows, searchText]);

  const prodPageItems = filteredProdDisplay.slice((prodPage - 1) * PROD_PAGE_SIZE, prodPage * PROD_PAGE_SIZE);
  const shipPageItems = filteredShipData.slice((shipPage - 1) * SHIP_PAGE_SIZE, shipPage * SHIP_PAGE_SIZE);

  if (detailStatus === "loading") {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="s" alignItems="center">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading detail data...</Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      {showOnly !== "shipment" && (
      <div data-testid="program-detail-production">
      <Container header={<Header variant="h2">{`Production — by ${TIME_LABEL[timeGranularity]}`}</Header>}>
        <SpaceBetween size="s">
          <div data-testid="program-table-production">
          <Table<ProdRow>
            filter={<div data-testid="program-table-production-search"><TextFilter filteringText={searchText} onChange={({ detail }) => { setSearchText(detail.filteringText); setProdPage(1); setShipPage(1); }} filteringPlaceholder="Search by program, CM or CM location..." /></div>}
            trackBy={(item: ProdRow) => `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`}
            columnDefinitions={[
              { id: "program", header: "Program", width: 180, cell: (item: ProdRow) => {
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
                const hasChildren = (prodChildrenMap.get(rowKey) || []).length > 0;
                if (hasChildren || item.parent_program) {
                  const childCount = (prodChildrenMap.get(rowKey) || []).length;
                  if (!item.parent_program && childCount > 0) {
                    return (
                      <span className="detail-expandable-program">
                        <span className="detail-program-cell">{item.program}</span>
                        <span className="detail-child-count-badge">{childCount}</span>
                      </span>
                    );
                  }
                  return <span className="detail-program-cell">{item.program}</span>;
                }
                const idx = prodPageItems.indexOf(item);
                const prevItem = prodPageItems[idx - 1];
                const groupChanged = idx === 0 || prevItem?.program !== item.program || prevItem?.cm !== item.cm || prevItem?.cm_location !== item.cm_location;
                return groupChanged ? <span className="detail-program-cell">{item.program}</span> : "";
              }},
              { id: "cm_location", header: "CM & Location", width: 100, cell: (item: ProdRow) => {
                if (item.parent_program) return "";
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
                const hasChildren = (prodChildrenMap.get(rowKey) || []).length > 0;
                if (hasChildren) {
                  return <span className="detail-cm-cell">{`${item.cm}-${item.cm_location}`}</span>;
                }
                const idx = prodPageItems.indexOf(item);
                const prevItem = prodPageItems[idx - 1];
                const groupChanged = idx === 0 || prevItem?.program !== item.program || prevItem?.cm !== item.cm || prevItem?.cm_location !== item.cm_location;
                return groupChanged ? <span className="detail-cm-cell">{`${item.cm}-${item.cm_location}`}</span> : "";
              }},
              { id: "stage", header: "Stage", width: 110, cell: (item: ProdRow) => {
                const match = (item.stage || "").match(/^(.+?)(\s*\(.*\))$/);
                if (match) {
                  return (
                    <span>
                      {match[1]}
                      <span style={{ color: "#888", fontSize: "0.85em" }}>{match[2]}</span>
                    </span>
                  );
                }
                return item.stage;
              }},
              { id: "plan", header: "Plan Qty", width: 90, cell: (item: ProdRow) => formatQty(item.plan) },
              { id: "actual", header: "Actual Qty", width: 90, cell: (item: ProdRow) => formatQty(item.actual) },
              { id: "gap", header: "Gap Qty", width: 80, cell: (item: ProdRow) => formatQty(item.gap) },
              { id: "cum", header: "Cum Qty", width: 90, cell: (item: ProdRow) => formatQty(item.cum) },
              { id: "hit_rate", header: "Hit Rate", width: 80, cell: (item: ProdRow) => formatRate(item.hit_rate) },
            ]}
            items={prodPageItems}
            expandableRows={{
              getItemChildren: (item: ProdRow) => {
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
                return prodChildrenMap.get(rowKey) || [];
              },
              isItemExpandable: (item: ProdRow) => {
                const rowKey = `${item.program}__${item.cm}__${item.cm_location}__${item.stage}`;
                return (prodChildrenMap.get(rowKey) || []).length > 0;
              },
              expandedItems: expandedProdItems,
              onExpandableItemToggle: ({ detail }) => {
                const item = detail.item;
                const isExpanded = detail.expanded;
                const getKey = (i: ProdRow) => `${i.program}__${i.cm}__${i.cm_location}__${i.stage}`;
                if (isExpanded) {
                  setExpandedProdItems((prev) => [...prev, item]);
                } else {
                  setExpandedProdItems((prev) => prev.filter((i) => getKey(i) !== getKey(item)));
                }
              },
            }}
            variant="embedded"
            stripedRows
            empty={<Box textAlign="center" color="inherit" padding={{ vertical: "l" }}><b>No production data</b><Box color="text-body-secondary" padding={{ top: "s" }}>No production records available for the selected period.</Box></Box>}
            pagination={
              <div className="detail-table-toolbar">
                <div className="detail-page-size-selector">
                  <span className="detail-page-size-label">Page size:</span>
                  <div className="detail-page-size-select">
                    <Select
                      selectedOption={selectedProdPageSize}
                      onChange={({ detail }) => { setSelectedProdPageSize(detail.selectedOption as SelectProps.Option); setProdPage(1); }}
                      options={PAGE_SIZE_OPTIONS}
                    />
                  </div>
                </div>
                {filteredProdDisplay.length > PROD_PAGE_SIZE && (
                  <div data-testid="program-table-production-pagination">
                    <Pagination currentPageIndex={prodPage} pagesCount={Math.ceil(filteredProdDisplay.length / PROD_PAGE_SIZE)} onChange={({ detail }) => setProdPage(detail.currentPageIndex)} />
                  </div>
                )}
              </div>
            }
          />
          </div>
        </SpaceBetween>
      </Container>
      </div>
      )}

      {showOnly !== "production" && (
      <div data-testid="program-detail-shipment">
      <Container header={<Header variant="h2">{`Shipment (PO) — by ${TIME_LABEL[timeGranularity]}`}</Header>}>
        <SpaceBetween size="s">
          <div data-testid="program-table-shipment">
          <Table
            filter={<TextFilter filteringText={searchText} onChange={({ detail }) => { setSearchText(detail.filteringText); setProdPage(1); setShipPage(1); }} filteringPlaceholder="Search by program, CM or CM location..." />}
            columnDefinitions={[
              { id: "program", header: "Program", width: 120, cell: (item: ShipRow) => {
                const idx = shipPageItems.indexOf(item);
                const prevItem = shipPageItems[idx - 1];
                const groupChanged = idx === 0 || prevItem?.program !== item.program || prevItem?.cm !== item.cm || prevItem?.cm_location !== item.cm_location;
                return groupChanged ? <span className="detail-program-cell">{item.program}</span> : "";
              }},
              { id: "cm_location", header: "CM & Location", width: 100, cell: (item: ShipRow) => {
                const idx = shipPageItems.indexOf(item);
                const prevItem = shipPageItems[idx - 1];
                const groupChanged = idx === 0 || prevItem?.program !== item.program || prevItem?.cm !== item.cm || prevItem?.cm_location !== item.cm_location;
                return groupChanged ? <span className="detail-cm-cell">{`${item.cm}-${item.cm_location}`}</span> : "";
              }},
              { id: "requested_shipment_qty", header: "Requested Qty", width: 110, cell: (item: ShipRow) => formatQty(item.requested_shipment_qty) },
              { id: "actual_shipment_qty", header: "Actual Qty", width: 100, cell: (item: ShipRow) => formatQty(item.actual_shipment_qty) },
              { id: "gap_shipment", header: "Gap Qty", width: 80, cell: (item: ShipRow) => formatQty(item.gap_shipment) },
              { id: "hit_rate", header: "OTD-C HR(%)", width: 90, cell: (item: ShipRow) => formatRate(item.hit_rate) },
              { id: "quantity_of_shipped", header: "Cum Qty", width: 100, cell: (item: ShipRow) => formatQty(item.quantity_of_shipped) },
            ]}
            items={shipPageItems}
            variant="embedded"
            stripedRows
            empty={<Box textAlign="center" color="inherit" padding={{ vertical: "l" }}><b>No shipment data</b><Box color="text-body-secondary" padding={{ top: "s" }}>No shipment records available for the selected period.</Box></Box>}
            pagination={
              <div className="detail-table-toolbar">
                <div className="detail-page-size-selector">
                  <span className="detail-page-size-label">Page size:</span>
                  <div className="detail-page-size-select">
                    <Select
                      selectedOption={selectedShipPageSize}
                      onChange={({ detail }) => { setSelectedShipPageSize(detail.selectedOption as SelectProps.Option); setShipPage(1); }}
                      options={PAGE_SIZE_OPTIONS}
                    />
                  </div>
                </div>
                {filteredShipData.length > SHIP_PAGE_SIZE && (
                  <div data-testid="program-table-shipment-pagination">
                    <Pagination currentPageIndex={shipPage} pagesCount={Math.ceil(filteredShipData.length / SHIP_PAGE_SIZE)} onChange={({ detail }) => setShipPage(detail.currentPageIndex)} />
                  </div>
                )}
              </div>
            }
          />
          </div>
        </SpaceBetween>
      </Container>
      </div>
      )}
    </SpaceBetween>
  );
};

export const ProductionOnlyDetail: React.FC<{ detailData?: ProgramDetailData | null; detailStatus?: RequestStatus; timeGranularity?: TimeGranularity }> = ({ detailData, detailStatus, timeGranularity }) => (
  <ProductionShipmentDetail showOnly="production" detailData={detailData} detailStatus={detailStatus} timeGranularity={timeGranularity} />
);

export const ShipmentOnlyDetail: React.FC<{ detailData?: ProgramDetailData | null; detailStatus?: RequestStatus; timeGranularity?: TimeGranularity }> = ({ detailData, detailStatus, timeGranularity }) => (
  <ProductionShipmentDetail showOnly="shipment" detailData={detailData} detailStatus={detailStatus} timeGranularity={timeGranularity} />
);
